<template>
  <section
    :class="['hero-section', `theme-${slides[currentSlide].theme}`, { 'is-loaded': isLoaded }]"
    ref="heroSection"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
    @mouseenter="handleMouseEnter"
  >
    <!-- 清爽、干净的渐变流动背景层 (Z: 1) -->
    <div class="hero-bg-container" :style="bgStyle">
      <!-- 极佳的背景图层切换，通过 opacity 渐变实现无缝丝滑过渡 -->
      <div
        v-for="(slide, idx) in slides"
        :key="idx"
        :class="['hero-bg-image-layer', `bg-layer-${slide.theme}`, { active: currentSlide === idx }]"
      >
        <div
          class="hero-bg-image"
          :style="[bgImageStyle, { backgroundImage: `url(/images/bg_tech_${slide.theme}.png)` }]"
        ></div>
      </div>
      <!-- 柔和的磨砂半透覆盖层，确保前景文字极佳的可读性与色彩过渡 -->
      <div class="hero-bg-overlay"></div>

      <div class="clean-grid-container">
        <div class="clean-grid grid-coarse"></div>
        <div class="clean-grid grid-fine"></div>
      </div>
      <div class="glow-orb orb-1"></div>
      <div class="glow-orb orb-2"></div>
      <div class="glow-orb orb-3"></div>
      <!-- 动态裸眼3D粒子网格画布 -->
      <canvas ref="heroCanvas" class="hero-canvas"></canvas>
    </div>

    <!-- 浮动发光微粒层 (Z: 2) - 强化景深与空间厚度 -->
    <div class="particle-container" :style="particleStyle">
      <span
        v-for="n in 20"
        :key="n"
        class="particle-dot"
        :style="getParticleStyle(n)"
      ></span>
    </div>

    <!-- 3D 视角卡片舞台容器 (Z: 3) -->
    <div class="hero-3d-card">
      <!-- 鼠标跟随反光折射层 (淡白色光泽) -->
      <div class="shine-overlay" :style="shineStyle"></div>

      <div class="container hero-content-grid">
        <!-- 左侧：文案与按钮操作区 (支持柔和3D视差，保证清晰度) -->
        <div class="text-content-wrapper" :style="textStyle">
          <transition name="slide-fade" mode="out-in">
            <div :key="currentSlide" class="slide-content-block">
              <div class="slide-badge">
                <span class="badge-dot"></span>
                <span class="badge-text">{{ slides[currentSlide].badge }}</span>
              </div>
              <!-- v-html 渲染以支持 Shimmer 流光特效 -->
              <h1 class="hero-title" v-html="slides[currentSlide].title"></h1>
              <p class="hero-subtitle">
                {{ slides[currentSlide].subtitle }}
              </p>
              <div class="hero-actions">
                <el-button type="primary" size="large" class="btn-glow" @click="handleLeftBtnClick">
                  {{ slides[currentSlide].btnLeftText }}
                </el-button>
                <el-button size="large" class="btn-outline" @click="handleRightBtnClick">
                  {{ slides[currentSlide].btnRightText }}
                </el-button>
              </div>

              <!-- 动态数据指标面板 Stats Grid -->
              <div class="hero-stats-grid">
                <div
                  v-for="(stat, sIdx) in slides[currentSlide].stats"
                  :key="sIdx"
                  class="stat-card"
                >
                  <div class="stat-card-glow"></div>
                  <div class="stat-value-group">
                    <span class="stat-value">{{ stat.value }}</span>
                  </div>
                  <div class="stat-label">{{ stat.label }}</div>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- 右侧：真 3D 空间，带立体旋转玻璃魔方与多维卡片视差 (Z-depth) -->
        <div class="hologram-wrapper">
          <div class="hologram-core-container" :style="hologramStyle">
            <!-- 3D 效果层：仅魔方与轨道进行 3D 旋转以防文本模糊 -->
            <div class="hologram-3d-stage" :style="hologram3DStyle">
              <!-- 3D 空间环绕轨道 -->
              <div class="holo-ring holo-ring-x"></div>
              <div class="holo-ring holo-ring-y"></div>
              <div class="holo-ring holo-ring-z"></div>

              <!-- 立体 3D 旋转玻璃魔方 -->
              <div class="cube-3d-wrapper">
                <div class="cube-3d">
                  <div class="cube-face face-front"></div>
                  <div class="cube-face face-back"></div>
                  <div class="cube-face face-left"></div>
                  <div class="cube-face face-right"></div>
                  <div class="cube-face face-top"></div>
                  <div class="cube-face face-bottom"></div>
                  <!-- 魔方内部的发光能量核 -->
                  <div class="cube-core"></div>
                </div>
              </div>
            </div>

            <!-- 浮动玻璃拟态卡片 1：Efficient (Z-depth: 110px) -->
            <div class="glass-card gc-1">
              <div class="glass-card-icon">
                <i class="el-icon-cpu"></i>
              </div>
              <div class="glass-card-info">
                <div class="glass-card-title">高效解决方案</div>
                <div class="glass-card-value-group">
                  <span class="glass-card-value">Load {{ cpuLoad }}%</span>
                  <!-- 动态自动绘制的 SVG 矢量折线图 -->
                  <svg class="mini-chart" viewBox="0 0 50 15" width="50" height="15">
                    <path d="M0,13 Q12,2 25,10 T50,2" fill="none" stroke="currentColor" stroke-width="1.8" class="chart-path" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- 浮动玻璃拟态卡片 2：Innovative (Z-depth: -60px) -->
            <div class="glass-card gc-2">
              <div class="glass-card-icon">
                <i class="el-icon-connection"></i>
              </div>
              <div class="glass-card-info">
                <div class="glass-card-title">全域赋能</div>
                <div class="glass-card-value-group">
                  <span class="glass-card-value">Speed {{ netSpeed }}M</span>
                  <!-- 动态旋转加载的进度环 -->
                  <svg class="mini-progress" viewBox="0 0 20 20" width="16" height="16">
                    <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(0,0,0,0.06)" stroke-width="2" />
                    <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="50" stroke-dashoffset="15" class="progress-path" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- 浮动玻璃拟态卡片 3：Secure (Z-depth: 50px) -->
            <div class="glass-card gc-3">
              <div class="glass-card-icon">
                <i class="el-icon-circle-check"></i>
              </div>
              <div class="glass-card-info">
                <div class="glass-card-title">安全与信赖</div>
                <div class="glass-card-value">Secure 99.99%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 轮播指示器 -->
    <div class="slider-indicators">
      <span
        v-for="(slide, index) in slides"
        :key="index"
        class="indicator"
        :class="{ active: currentSlide === index }"
        @click="goToSlide(index)"
      ></span>
    </div>
  </section>
</template>

<script>
const THEME_COLORS = {
  cyan: { primary: [37, 99, 235], secondary: [6, 182, 212] },
  purple: { primary: [124, 58, 237], secondary: [217, 70, 239] },
  teal: { primary: [15, 118, 110], secondary: [16, 185, 129] },
  gold: { primary: [217, 119, 6], secondary: [245, 158, 11] }
}

export default {
  name: 'HeroSection',
  data () {
    return {
      currentSlide: 0,
      slides: [
        {
          badge: 'Technology & Innovation',
          title: '创新科技，<span class="text-shimmer">智启未来</span>',
          subtitle: '以人工智能、区块链及大数据技术为核心，倾力打造下一代数字化转型标杆，助您在数字浪潮中抢占先机。',
          theme: 'cyan',
          btnLeftText: '了解产品',
          btnRightText: '联系我们',
          stats: [
            { value: '120+', label: '客户交付项目' },
            { value: '85%+', label: '核心技术研发占比' }
          ]
        },
        {
          badge: 'Digital Transformation',
          title: '数字化转型，<span class="text-shimmer">全域赋能</span>',
          subtitle: '深耕企业核心场景，构建高可用、高并发的技术底座，以微服务和云原生技术重构企业业务敏捷流。',
          theme: 'purple',
          btnLeftText: '关于我们',
          btnRightText: '最新动态',
          stats: [
            { value: '45+', label: '核心自主知识产权' },
            { value: '25+', label: '覆盖行业解决方案' }
          ]
        },
        {
          badge: 'Custom Intelligence',
          title: '定制智能，<span class="text-shimmer">精准交付</span>',
          subtitle: '深入挖掘业务痛点，提供从系统架构规划、全端开发到运维保障的端到端、一站式高品质定制开发服务。',
          theme: 'teal',
          btnLeftText: '定制咨询',
          btnRightText: '成功案例',
          stats: [
            { value: '<200ms', label: '核心接口平均响应' },
            { value: '100%', label: '项目准时交付率' }
          ]
        },
        {
          badge: 'Global Guarantee',
          title: '全天候保障，<span class="text-shimmer">信赖无忧</span>',
          subtitle: '引入金融级安全合规底座与全链路监控系统，配合7x24小时全时段技术支持，为系统平稳运行护航。',
          theme: 'gold',
          btnLeftText: '业务合作',
          btnRightText: '服务体系',
          stats: [
            { value: '99.99%', label: '平台系统可用性' },
            { value: '5分钟', label: '紧急故障响应' }
          ]
        }
      ],
      slideInterval: null,

      // 鼠标交互位置数据
      mouseX: 0,
      mouseY: 0,
      isHovered: false,

      // 缓动插值中的当前渲染数值 (用于 3D 旋转与平移)
      rotateX: 0,
      rotateY: 0,
      translateX: 0,
      translateY: 0,

      // 动画帧句柄
      rAF: null,

      // 进场动画状态
      isLoaded: false,
      isAnimationFinished: false,
      loadedTimeout: null,
      finishTimeout: null,

      // 实时小幅波动指标
      cpuLoad: 42.5,
      netSpeed: 75.8,
      updateInterval: null,

      // 3D 粒子及颜色渐变状态
      currentColors: {
        primary: [37, 99, 235],
        secondary: [6, 182, 212]
      },
      particles: [],
      canvasWidth: 0,
      canvasHeight: 0
    }
  },
  computed: {
    bgStyle () {
      if (!this.isAnimationFinished) {
        return {}
      }
      const extraTx = -this.translateX * 0.7
      const extraTy = -this.translateY * 0.7
      const extraRx = -this.rotateX * 0.35
      const extraRy = -this.rotateY * 0.35
      return {
        transform: `translate3d(${extraTx}px, ${extraTy}px, -120px) rotateX(${extraRx}deg) rotateY(${extraRy}deg)`,
        transformStyle: 'preserve-3d'
      }
    },
    bgImageStyle () {
      if (!this.isAnimationFinished) {
        return {}
      }
      const extraTx = this.translateX * 0.4
      const extraTy = this.translateY * 0.4
      return {
        // 反向平移以抵消外层容器的部分位移，在屏幕空间形成较慢的位移 (-0.3x)，与中层网格、粒子拉开层次
        transform: `translate(${extraTx}px, ${extraTy}px) scale(1.08)`
      }
    },
    particleStyle () {
      if (!this.isAnimationFinished) {
        return {}
      }
      const extraTx = this.translateX * 0.3
      const extraTy = this.translateY * 0.3
      return {
        transform: `translate3d(${extraTx}px, ${extraTy}px, -50px)`,
        pointerEvents: 'none'
      }
    },
    textStyle () {
      if (!this.isAnimationFinished) {
        return {}
      }
      const extraTx = this.translateX * 0.5
      const extraTy = this.translateY * 0.5
      return {
        // 使用 2D 平移，防止 3D 偏转与深度导致左侧文字（标题、按钮等）渲染变糊
        transform: `translate(${extraTx}px, ${extraTy}px)`
      }
    },
    hologramStyle () {
      // 动画未完前，只应用配色，不应用 inline translate 以免覆盖进场 CSS 动画
      if (!this.isAnimationFinished) {
        return {
          color: 'var(--theme-primary)'
        }
      }
      const extraTx = this.translateX * 1.5
      const extraTy = this.translateY * 1.5
      return {
        // 全息核心容器改用 2D 平移，隔离 rotateX/Y 对内部浮动卡片和 HUD 文本的模糊影响
        // 并通过 CSS 变量 --hologram-scale 完美兼容移动端缩放
        transform: `translate(${extraTx}px, ${extraTy}px) scale(var(--hologram-scale, 1))`,
        color: 'var(--theme-primary)'
      }
    },
    hologram3DStyle () {
      if (!this.isAnimationFinished) {
        return {}
      }
      // 仅在此处对魔方和轨道应用 3D 视角与旋转偏转，保留完美的科技空间感
      return {
        transform: `perspective(1000px) rotateX(${this.rotateX}deg) rotateY(${this.rotateY}deg)`,
        transformStyle: 'preserve-3d'
      }
    },
    shineStyle () {
      const xPercent = 50 + this.mouseX * 100
      const yPercent = 50 + this.mouseY * 100
      return {
        background: `radial-gradient(circle at ${xPercent}% ${yPercent}%, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0) 55%)`
      }
    }
  },
  mounted () {
    this.startSlideShow()
    this.initCanvas()
    this.animate()

    // 触发进场时序动画
    this.$nextTick(() => {
      this.loadedTimeout = setTimeout(() => {
        this.isLoaded = true
      }, 50)

      this.finishTimeout = setTimeout(() => {
        this.isAnimationFinished = true
      }, 1800)
    })

    // 每秒小幅更新系统数值
    this.updateInterval = setInterval(() => {
      this.cpuLoad = +(38 + Math.sin(Date.now() / 1000) * 4 + Math.random() * 2).toFixed(1)
      this.netSpeed = +(72 + Math.cos(Date.now() / 1500) * 6 + Math.random() * 3).toFixed(1)
    }, 1000)
  },
  beforeDestroy () {
    this.stopSlideShow()
    window.removeEventListener('resize', this.resizeCanvas)
    if (this.rAF) {
      cancelAnimationFrame(this.rAF)
    }
    if (this.loadedTimeout) {
      clearTimeout(this.loadedTimeout)
    }
    if (this.finishTimeout) {
      clearTimeout(this.finishTimeout)
    }
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    startSlideShow () {
      this.slideInterval = setInterval(() => {
        this.nextSlide()
      }, 7000)
    },
    stopSlideShow () {
      if (this.slideInterval) {
        clearInterval(this.slideInterval)
      }
    },
    nextSlide () {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length
    },
    goToSlide (index) {
      this.currentSlide = index
      this.stopSlideShow()
      this.startSlideShow()
    },
    handleMouseMove (e) {
      const rect = this.$refs.heroSection.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width - 0.5
      const y = (e.clientY - rect.top) / rect.height - 0.5

      this.mouseX = x
      this.mouseY = y
    },
    handleMouseLeave () {
      this.isHovered = false
      this.mouseX = 0
      this.mouseY = 0
    },
    handleMouseEnter () {
      this.isHovered = true
    },
    animate () {
      const maxRotateX = 13
      const maxRotateY = 15
      const maxTranslateX = 28
      const maxTranslateY = 28

      const targetRotateX = -this.mouseY * maxRotateX
      const targetRotateY = this.mouseX * maxRotateY
      const targetTranslateX = this.mouseX * maxTranslateX
      const targetTranslateY = this.mouseY * maxTranslateY

      const lerp = 0.08
      this.rotateX += (targetRotateX - this.rotateX) * lerp
      this.rotateY += (targetRotateY - this.rotateY) * lerp
      this.translateX += (targetTranslateX - this.translateX) * lerp
      this.translateY += (targetTranslateY - this.translateY) * lerp

      // 实时重绘 3D 粒子画布
      this.drawCanvas()

      this.rAF = requestAnimationFrame(this.animate)
    },
    initCanvas () {
      const canvas = this.$refs.heroCanvas
      if (!canvas) return
      this.ctx = canvas.getContext('2d')
      this.resizeCanvas()
      window.addEventListener('resize', this.resizeCanvas)
      this.createParticles()
    },
    resizeCanvas () {
      const canvas = this.$refs.heroCanvas
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      const targetWidth = Math.round(rect.width * dpr)
      const targetHeight = Math.round(rect.height * dpr)

      // 仅当尺寸真正发生改变时才更新，防止移动端浏览器因滚动条显隐等重复执行 resize 导致反复清除画布而闪烁
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth
        canvas.height = targetHeight
        if (this.ctx) {
          this.ctx.scale(dpr, dpr)
        }
        this.canvasWidth = rect.width
        this.canvasHeight = rect.height
      }
    },
    createParticles () {
      // 移动端缩减粒子数量以极大提升渲染与连线计算性能，降低 CPU/GPU 负担并防止卡顿闪烁
      const isMobile = window.innerWidth <= 768
      const count = isMobile ? 35 : 75
      this.particles = []
      for (let i = 0; i < count; i++) {
        this.particles.push({
          x: (Math.random() - 0.5) * 1200,
          y: (Math.random() - 0.5) * 800,
          z: Math.random() * 800 + 100, // Z轴深度，范围 100 - 900
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          vz: (Math.random() - 0.5) * 0.25,
          radius: Math.random() * 1.6 + 0.8
        })
      }
    },
    drawCanvas () {
      const ctx = this.ctx
      if (!ctx) return

      // 清除画布
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)

      const centerX = this.canvasWidth / 2
      const centerY = this.canvasHeight / 2
      const focalLength = 380

      // 计算视差鼠标偏移量
      const mouseShiftX = this.translateX * 2.2
      const mouseShiftY = this.translateY * 2.2

      // 渐变过渡主题颜色
      const targetColors = THEME_COLORS[this.slides[this.currentSlide].theme] || THEME_COLORS.cyan
      const colorLerp = 0.05
      for (let i = 0; i < 3; i++) {
        this.currentColors.primary[i] += (targetColors.primary[i] - this.currentColors.primary[i]) * colorLerp
        this.currentColors.secondary[i] += (targetColors.secondary[i] - this.currentColors.secondary[i]) * colorLerp
      }

      const pColor = `rgba(${Math.round(this.currentColors.primary[0])}, ${Math.round(this.currentColors.primary[1])}, ${Math.round(this.currentColors.primary[2])}, `
      const sColor = `rgba(${Math.round(this.currentColors.secondary[0])}, ${Math.round(this.currentColors.secondary[1])}, ${Math.round(this.currentColors.secondary[2])}, `

      // 1. 投影与运动更新
      const projected = []
      for (let i = 0; i < this.particles.length; i++) {
        const p = this.particles[i]

        // 运动更新
        p.x += p.vx
        p.y += p.vy
        p.z += p.vz

        // 边界循环处理
        const rangeX = 600
        const rangeY = 400
        if (p.x < -rangeX) { p.x = rangeX; p.vx = (Math.random() - 0.5) * 0.4; }
        if (p.x > rangeX) { p.x = -rangeX; p.vx = (Math.random() - 0.5) * 0.4; }
        if (p.y < -rangeY) { p.y = rangeY; p.vy = (Math.random() - 0.5) * 0.4; }
        if (p.y > rangeY) { p.y = -rangeY; p.vy = (Math.random() - 0.5) * 0.4; }
        if (p.z < 100) { p.z = 900; p.vz = (Math.random() - 0.5) * 0.25; }
        if (p.z > 900) { p.z = 100; p.vz = (Math.random() - 0.5) * 0.25; }

        // 3D 视差透视公式
        const zDepth = p.z
        const scale = focalLength / (focalLength + zDepth)

        // 背景视差：与鼠标相反方向移动，实现极强的景深拉开效果
        const xOffset = -mouseShiftX * (1.1 - scale)
        const yOffset = -mouseShiftY * (1.1 - scale)

        const px = centerX + (p.x + xOffset) * scale
        const py = centerY + (p.y + yOffset) * scale

        // 根据 Z 轴深度计算不透明度 (近处亮，远处暗)
        const opacity = (1 - (p.z - 100) / 800) * 0.28

        projected.push({
          x: px,
          y: py,
          z: p.z,
          opacity: opacity,
          radius: p.radius * scale * 1.5,
          color: i % 2 === 0 ? pColor : sColor
        })
      }

      // 2. 绘制粒子连线 (网络星空效果)
      for (let i = 0; i < projected.length; i++) {
        const p1 = projected[i]
        for (let j = i + 1; j < projected.length; j++) {
          const p2 = projected[j]

          const dx = p1.x - p2.x
          const dy = p1.y - p2.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          const zDiff = Math.abs(p1.z - p2.z)

          // 仅当 2D 距离和 Z 轴深度差都在设定阈值内时，才进行连线
          if (dist < 120 && zDiff < 140) {
            const lineOpacity = (1 - dist / 120) * (1 - zDiff / 140) * Math.min(p1.opacity, p2.opacity) * 0.35
            if (lineOpacity > 0.01) {
              ctx.beginPath()
              ctx.moveTo(p1.x, p1.y)
              ctx.lineTo(p2.x, p2.y)

              // 绘制双色过渡渐变线
              const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y)
              grad.addColorStop(0, p1.color + lineOpacity + ')')
              grad.addColorStop(1, p2.color + lineOpacity + ')')

              ctx.strokeStyle = grad
              ctx.lineWidth = 0.6
              ctx.stroke()
            }
          }
        }
      }

      // 3. 绘制粒子节点
      for (let i = 0; i < projected.length; i++) {
        const p = projected[i]

        ctx.beginPath()
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
        ctx.fillStyle = p.color + p.opacity + ')'
        ctx.fill()

        // 给予近处较大的粒子额外的晕光效果，增强氛围感
        if (p.z < 280) {
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.radius * 2.8, 0, Math.PI * 2)
          ctx.fillStyle = p.color + (p.opacity * 0.25) + ')'
          ctx.fill()
        }
      }
    },
    getParticleStyle (n) {
      const size = (n * 3 % 5) + 3
      const left = (n * 17 % 100)
      const delay = -(n * 7 % 20)
      const duration = (n * 4 % 8) + 12
      const opacity = ((n * 3 % 15) / 100) + 0.05
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        opacity: opacity,
        backgroundColor: 'var(--theme-primary)',
        transition: 'background-color 1.2s ease'
      }
    },
    handleLeftBtnClick () {
      const theme = this.slides[this.currentSlide].theme
      if (theme === 'cyan') {
        this.goToProducts()
      } else if (theme === 'purple') {
        this.goToAbout()
      } else if (theme === 'teal') {
        this.goToContact()
      } else if (theme === 'gold') {
        this.goToContact()
      }
    },
    handleRightBtnClick () {
      const theme = this.slides[this.currentSlide].theme
      if (theme === 'cyan') {
        this.goToContact()
      } else if (theme === 'purple') {
        this.goToNews()
      } else if (theme === 'teal') {
        this.goToProducts()
      } else if (theme === 'gold') {
        this.goToAbout()
      }
    },
    goToProducts () {
      this.$router.push('/products')
    },
    goToContact () {
      this.$router.push('/contact')
    },
    goToAbout () {
      this.$router.push('/about')
    },
    goToNews () {
      this.$router.push('/news')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.hero-section {
  position: relative;
  height: 100vh;
  overflow: hidden;
  color: $text-primary;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1200px;

  --neon-cyan: #2563eb;
  --neon-purple: #7c3aed;

  --theme-primary: #2563eb;
  --theme-secondary: #06b6d4;
  --theme-primary-rgb: 37, 99, 235;
  --theme-secondary-rgb: 6, 182, 212;
  --theme-glow: rgba(37, 99, 235, 0.15);
  --theme-glow-light: rgba(6, 182, 212, 0.08);
  --theme-btn-gradient: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
  --hologram-scale: 1;

  // 基础背景渐变 fallback
  background: linear-gradient(135deg, #ffffff 0%, #f9fbfd 50%, #f4f8fc 100%);
  transition: color 1.2s ease;

  &.theme-cyan {
    --theme-primary: #2563eb;
    --theme-secondary: #06b6d4;
    --theme-primary-rgb: 37, 99, 235;
    --theme-secondary-rgb: 6, 182, 212;
    --theme-glow: rgba(37, 99, 235, 0.15);
    --theme-glow-light: rgba(6, 182, 212, 0.06);
    --theme-btn-gradient: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
  }

  &.theme-purple {
    --theme-primary: #7c3aed;
    --theme-secondary: #d946ef;
    --theme-primary-rgb: 124, 58, 237;
    --theme-secondary-rgb: 217, 70, 239;
    --theme-glow: rgba(124, 58, 237, 0.15);
    --theme-glow-light: rgba(217, 70, 239, 0.06);
    --theme-btn-gradient: linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%);
  }

  &.theme-teal {
    --theme-primary: #0f766e;
    --theme-secondary: #10b981;
    --theme-primary-rgb: 15, 118, 110;
    --theme-secondary-rgb: 16, 185, 129;
    --theme-glow: rgba(15, 118, 110, 0.15);
    --theme-glow-light: rgba(16, 185, 129, 0.06);
    --theme-btn-gradient: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
  }

  &.theme-gold {
    --theme-primary: #d97706;
    --theme-secondary: #f59e0b;
    --theme-primary-rgb: 217, 119, 6;
    --theme-secondary-rgb: 245, 158, 11;
    --theme-glow: rgba(217, 119, 6, 0.15);
    --theme-glow-light: rgba(245, 158, 11, 0.06);
    --theme-btn-gradient: linear-gradient(135deg, #b45309 0%, #d97706 100%);
  }
}

@keyframes liquidBg {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* ==========================================================================
   1. 背景层与漂浮微粒
   ========================================================================== */
.hero-bg-container {
  position: absolute;
  top: -10%;
  left: -10%;
  width: 120%;
  height: 120%;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
}

.hero-bg-image-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 1.8s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 1;

  &.active {
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
  }

  &.bg-layer-cyan::after {
    background-image:
      radial-gradient(circle at 10% 20%, rgba(37, 99, 235, 0.05) 0%, transparent 45%),
      radial-gradient(circle at 90% 10%, rgba(6, 182, 212, 0.04) 0%, transparent 45%);
  }

  &.bg-layer-purple::after {
    background-image:
      radial-gradient(circle at 15% 15%, rgba(124, 58, 237, 0.05) 0%, transparent 45%),
      radial-gradient(circle at 85% 25%, rgba(217, 70, 239, 0.04) 0%, transparent 45%);
  }

  &.bg-layer-teal::after {
    background-image:
      radial-gradient(circle at 10% 25%, rgba(15, 118, 110, 0.05) 0%, transparent 45%),
      radial-gradient(circle at 90% 15%, rgba(16, 185, 129, 0.04) 0%, transparent 45%);
  }

  &.bg-layer-gold::after {
    background-image:
      radial-gradient(circle at 20% 10%, rgba(217, 119, 6, 0.04) 0%, transparent 45%),
      radial-gradient(circle at 80% 30%, rgba(245, 158, 11, 0.03) 0%, transparent 45%);
  }
}

.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
  transform: scale(1);

  .active & {
    animation: bgFloatZoom 36s ease-in-out infinite alternate;
  }
}

@keyframes bgFloatZoom {
  0% { transform: scale(1) translate(0, 0); }
  50% { transform: scale(1.04) translate(-1%, -0.5%); }
  100% { transform: scale(1.02) translate(0.5%, 0.5%); }
}

.hero-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.3) 100%);
  pointer-events: none;
  z-index: 2;
}

.hero-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.85;
  z-index: 5;
}

.clean-grid-container {
  position: absolute;
  width: 100%;
  height: 100%;
  mask-image: radial-gradient(circle at center, black 30%, transparent 80%);
  -webkit-mask-image: radial-gradient(circle at center, black 30%, transparent 80%);
  z-index: 3;
}

.clean-grid {
  position: absolute;
  width: 100%;
  height: 100%;
  background-position: center;
  transform: rotateX(60deg) scale(1.6) translateY(-10%);
  animation: gridScroll 35s linear infinite;

  opacity: 0;
  transition: opacity 2.5s ease;

  &.grid-coarse {
    background-image:
      linear-gradient(rgba(var(--theme-primary-rgb), 0.02) 1px, transparent 1px),
      linear-gradient(90deg, rgba(var(--theme-primary-rgb), 0.02) 1px, transparent 1px);
    background-size: 80px 80px;

    .is-loaded & {
      opacity: 0.8;
      transition-delay: 0.1s;
    }
  }

  &.grid-fine {
    background-image:
      linear-gradient(rgba(var(--theme-primary-rgb), 0.008) 1px, transparent 1px),
      linear-gradient(90deg, rgba(var(--theme-primary-rgb), 0.008) 1px, transparent 1px);
    background-size: 20px 20px;

    .is-loaded & {
      opacity: 0.6;
      transition-delay: 0.1s;
    }
  }
}

@keyframes gridScroll {
  0% { background-position: 0 0; }
  100% { background-position: 0 1200px; }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(140px);
  pointer-events: none;
  transition: opacity 2.8s cubic-bezier(0.16, 1, 0.3, 1), transform 2.8s cubic-bezier(0.16, 1, 0.3, 1), background 1.5s ease-in-out;
  z-index: 4;

  opacity: 0;
  transform: scale(0.6);

  .is-loaded & {
    opacity: 0.14;
    transform: scale(1);
  }

  &.orb-1 {
    top: 5%;
    left: 10%;
    width: 550px;
    height: 550px;
    background: radial-gradient(circle, var(--theme-primary) 0%, transparent 70%);
    animation: orbFloat1 22s ease-in-out infinite;
    animation-delay: 2.8s;
  }
  &.orb-2 {
    bottom: 5%;
    right: 10%;
    width: 650px;
    height: 650px;
    background: radial-gradient(circle, var(--theme-secondary) 0%, transparent 70%);
    animation: orbFloat2 27s ease-in-out infinite;
    animation-delay: 2.8s;
  }
  &.orb-3 {
    top: 20%;
    right: 35%;
    width: 450px;
    height: 450px;
    background: radial-gradient(circle, var(--theme-glow-light) 0%, transparent 75%);
    animation: orbFloat3 19s ease-in-out infinite;
    animation-delay: 2.8s;
  }
}

@keyframes orbFloat1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(60px, 40px) scale(1.1); }
}
@keyframes orbFloat2 {
  0%, 100% { transform: translate(0, 0) scale(1.05); }
  50% { transform: translate(-70px, -50px) scale(0.95); }
}
@keyframes orbFloat3 {
  0%, 100% { transform: translate(0, 0) scale(0.95); }
  50% { transform: translate(50px, -60px) scale(1.15); }
}

.particle-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  overflow: hidden;
}

.particle-dot {
  position: absolute;
  border-radius: 50%;
  bottom: -20px;
  animation: floatUp 15s linear infinite;
  pointer-events: none;
}

@keyframes floatUp {
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0;
  }
  15% { opacity: 0.8; }
  85% { opacity: 0.8; }
  100% {
    transform: translateY(-110vh) translateX(70px);
    opacity: 0;
  }
}

/* ==========================================================================
   2. 3D 视角舞台与结构
   ========================================================================== */
.hero-3d-card {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 3;
  display: flex;
  align-items: center;
  transform-style: preserve-3d;
}

.shine-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

.hero-content-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  align-items: center;
  gap: 50px;
  width: 100%;
  padding: 0 50px;
  transform-style: preserve-3d;
}

/* ==========================================================================
   3. 左侧：文案内容与操作 (纯 2D 硬件分层，确保像素级清晰度)
   ========================================================================== */
.text-content-wrapper {
  z-index: 5;
  transform: translateZ(0); // 隔离 3D 偏转产生的文字栅格化模糊
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.slide-content-block {
  text-align: left;
}

.slide-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(var(--theme-primary-rgb), 0.15);
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--theme-primary);
  letter-spacing: 1.2px;
  margin-bottom: 24px;
  text-transform: uppercase;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.01);

  opacity: 0;
  transform: translateY(-20px);
  transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), border-color 1.2s ease, color 1.2s ease;

  .is-loaded & {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 0.2s, 0.2s, 0s, 0s;
  }
}

.badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--theme-primary);
  box-shadow: 0 0 6px var(--theme-primary);
  animation: flash 1.5s infinite alternate;
  transition: background-color 1.2s ease, box-shadow 1.2s ease;
}

@keyframes flash {
  0% { opacity: 0.4; }
  100% { opacity: 1; }
}

.hero-title {
  font-size: 3.6rem;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.5px;

  opacity: 0;
  transform: translateY(30px) skewY(1deg);
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);

  .is-loaded & {
    opacity: 1;
    transform: translateY(0) skewY(0deg);
    transition-delay: 0.35s;
  }
}

// 标题内部的流光文字样式
::v-class, ::v-deep {
  .text-shimmer {
    background: linear-gradient(
      120deg,
      var(--theme-primary) 20%,
      var(--theme-secondary) 40%,
      var(--theme-primary) 60%,
      var(--theme-secondary) 80%,
      var(--theme-primary) 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: textShimmer 3.5s linear infinite;
    font-weight: 800;
    text-shadow: 0 0 12px var(--theme-glow-light);
    display: inline-block;
  }
}

@keyframes textShimmer {
  0% { background-position: 0% center; }
  100% { background-position: -200% center; }
}

.hero-subtitle {
  font-size: 1.15rem;
  line-height: 1.8;
  color: #475569;
  margin-bottom: 38px;
  max-width: 620px;

  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);

  .is-loaded & {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 0.48s;
  }
}

.hero-actions {
  display: flex;
  gap: 20px;

  opacity: 0;
  transform: translateY(15px);
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);

  .is-loaded & {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 0.6s;
  }
}

.btn-glow {
  background: var(--theme-btn-gradient) !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 0.95rem !important;
  padding: 12px 28px !important;
  border-radius: 8px !important;
  letter-spacing: 1px;
  box-shadow: 0 4px 14px var(--theme-glow) !important;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px var(--theme-glow) !important;
  }
}

.btn-outline {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  color: #475569 !important;
  font-weight: 600 !important;
  font-size: 0.95rem !important;
  padding: 12px 28px !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02) !important;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;

  &:hover {
    border-color: var(--theme-primary) !important;
    color: var(--theme-primary) !important;
    background: #ffffff !important;
    transform: translateY(-2px);
  }
}

.hero-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 48px;
  max-width: 580px;

  .stat-card {
    position: relative;
    background: rgba(255, 255, 255, 0.45);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.7);
    border-radius: 12px;
    padding: 16px 22px;
    box-shadow:
      0 4px 20px rgba(0, 0, 0, 0.01),
      inset 0 0 0 1px rgba(255, 255, 255, 0.6);
    overflow: hidden;

    opacity: 0;
    transform: translateY(30px);
    transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1.2s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.4s, box-shadow 0.4s;

    .is-loaded & {
      opacity: 1;
      transform: translateY(0);

      &:nth-child(1) {
        transition-delay: 0.75s, 0.75s, 0s, 0s;
      }
      &:nth-child(2) {
        transition-delay: 0.9s, 0.9s, 0s, 0s;
      }
    }

    .stat-card-glow {
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, var(--theme-glow-light) 0%, transparent 60%);
      opacity: 0;
      transition: opacity 0.4s ease;
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-5px);
      border-color: var(--theme-primary);
      box-shadow:
        0 12px 28px var(--theme-glow),
        0 2px 4px rgba(0, 0, 0, 0.02);

      .stat-card-glow {
        opacity: 1;
      }
    }
  }

  .stat-value {
    font-size: 2.1rem;
    font-weight: 800;
    line-height: 1.1;
    font-family: 'Outfit', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
    background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
  }

  .stat-label {
    font-size: 0.85rem;
    font-weight: 550;
    color: #64748b;
    margin-top: 6px;
    letter-spacing: 0.5px;
  }
}

.slide-fade-enter-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-fade-enter {
  transform: translateY(24px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-24px);
  opacity: 0;
}

/* ==========================================================================
   4. 右侧：真 3D 空间，带立体旋转玻璃魔方与多维卡片视差 (Z-depth)
   ========================================================================== */
.hologram-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  z-index: 4;
  transform-style: preserve-3d;
}

.hologram-core-container {
  position: relative;
  width: 320px;
  height: 320px;
  transform-style: preserve-3d;
  animation: floatHolo 6s ease-in-out infinite;
  animation-delay: 1.8s;

  opacity: 0;
  transform: scale(0.7) translate3d(0, 50px, 0);
  transition: opacity 1.6s cubic-bezier(0.16, 1, 0.3, 1), transform 1.6s cubic-bezier(0.16, 1, 0.3, 1), color 1.2s ease;

  .is-loaded & {
    opacity: 1;
    transform: scale(var(--hologram-scale, 1)) translate3d(0, 0, 0);
    transition-delay: 0.4s, 0.4s, 0s;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: -45px;
    left: 15%;
    width: 70%;
    height: 20px;
    background: radial-gradient(ellipse, var(--theme-glow) 0%, transparent 70%);
    filter: blur(8px);
    animation: shadowPulse 6s ease-in-out infinite;
    animation-delay: 1.8s;
    transition: background 1.2s ease;
  }
}

@keyframes floatHolo {
  0%, 100% { transform: translateY(0px) rotateY(0deg); }
  50% { transform: translateY(-14px) rotateY(2deg); }
}

@keyframes shadowPulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(0.85); opacity: 0.4; }
}

.hologram-3d-stage {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transform-style: preserve-3d;
  pointer-events: none;
}

.holo-ring {
  position: absolute;
  top: -10%;
  left: -10%;
  width: 120%;
  height: 120%;
  border-radius: 50%;
  border: 1.5px solid rgba(var(--theme-primary-rgb), 0.12);
  pointer-events: none;
  transition: border-color 1.2s ease, opacity 1.5s ease;
  box-shadow: inset 0 0 20px rgba(var(--theme-primary-rgb), 0.05), 0 0 20px rgba(var(--theme-primary-rgb), 0.05);

  opacity: 0;

  .is-loaded & {
    opacity: 0.35;
    transition-delay: 0.8s;
  }

  &.holo-ring-x {
    transform: rotateX(80deg);
    animation: spinRingX 25s linear infinite;
    border-color: rgba(var(--theme-primary-rgb), 0.18);
  }

  &.holo-ring-y {
    transform: rotateY(80deg);
    animation: spinRingY 30s linear infinite;
    border-color: rgba(var(--theme-secondary-rgb), 0.15);
  }

  &.holo-ring-z {
    transform: rotateZ(45deg);
    border-style: solid;
    border-width: 1px;
    border-color: rgba(var(--theme-primary-rgb), 0.1);
    animation: spinRingZ 35s linear infinite;
  }
}

@keyframes spinRingX {
  0% { transform: rotateX(80deg) rotateZ(0deg); }
  100% { transform: rotateX(80deg) rotateZ(360deg); }
}
@keyframes spinRingY {
  0% { transform: rotateY(80deg) rotateZ(0deg); }
  100% { transform: rotateY(80deg) rotateZ(-360deg); }
}
@keyframes spinRingZ {
  0% { transform: rotateZ(45deg) rotateX(20deg); }
  100% { transform: rotateZ(405deg) rotateX(20deg); }
}

.cube-3d-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 180px;
  height: 180px;
  margin-top: -90px;
  margin-left: -90px;
  transform-style: preserve-3d;
  transform: translateZ(10px);
  pointer-events: none;
}

.cube-3d {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: rotateCube 22s linear infinite;
}

@keyframes rotateCube {
  0% { transform: rotateX(25deg) rotateY(0deg) rotateZ(0deg); }
  100% { transform: rotateX(25deg) rotateY(360deg) rotateZ(360deg); }
}

.cube-face {
  position: absolute;
  width: 180px;
  height: 180px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-top-color: rgba(255, 255, 255, 0.7);
  border-left-color: rgba(255, 255, 255, 0.7);
  opacity: 0.9;
  box-shadow:
    inset 0 0 18px rgba(255, 255, 255, 0.2),
    0 0 15px rgba(var(--theme-primary-rgb), 0.08);
  transition: border-color 1.2s ease, box-shadow 1.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 60%, transparent 100%);
    pointer-events: none;
  }

  &.face-front  { transform: rotateY(  0deg) translateZ(90px); }
  &.face-back   { transform: rotateY(180deg) translateZ(90px); }
  &.face-left   { transform: rotateY(-90deg) translateZ(90px); }
  &.face-right  { transform: rotateY( 90deg) translateZ(90px); }
  &.face-top    { transform: rotateX( 90deg) translateZ(90px); }
  &.face-bottom { transform: rotateX(-90deg) translateZ(90px); }
}

.cube-core {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 48px;
  height: 48px;
  margin-top: -24px;
  margin-left: -24px;
  border-radius: 50%;
  background: radial-gradient(circle, #ffffff 10%, rgba(var(--theme-primary-rgb), 0.8) 50%, transparent 100%);
  box-shadow: 0 0 35px var(--theme-primary), inset 0 0 12px rgba(255, 255, 255, 0.5);
  animation: corePulse 2s ease-in-out infinite alternate;
  transform: translateZ(0);
  filter: drop-shadow(0 0 8px currentColor);
  transition: color 1.2s ease;
}

@keyframes corePulse {
  0% { transform: scale(0.85); opacity: 0.7; filter: drop-shadow(0 0 5px currentColor); }
  100% { transform: scale(1.2); opacity: 1; filter: drop-shadow(0 0 12px currentColor); }
}

/* 4.2 浮动磨砂玻璃卡片 */
.glass-card {
  position: absolute;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.58) 0%, rgba(255, 255, 255, 0.18) 100%);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-top-color: rgba(255, 255, 255, 0.65);
  border-left-color: rgba(255, 255, 255, 0.65);
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow:
    0 12px 40px rgba(15, 23, 42, 0.05),
    0 1px 3px rgba(0, 0, 0, 0.01),
    inset 0 0 12px rgba(255, 255, 255, 0.55);
  pointer-events: auto;
  transition: opacity 1.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.4s, box-shadow 0.4s;
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;

  opacity: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 60%, transparent 100%);
    pointer-events: none;
  }

  .glass-card-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(var(--theme-primary-rgb), 0.12) 0%, rgba(var(--theme-primary-rgb), 0.03) 100%);
    border: 1px solid rgba(var(--theme-primary-rgb), 0.15);
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 19px;
    color: var(--theme-primary);
    box-shadow: inset 0 0 6px rgba(var(--theme-primary-rgb), 0.05);
    transition: color 1.2s ease, background 1.2s ease, border-color 1.2s, box-shadow 1.2s;
  }

  .glass-card-info {
    text-align: left;
  }

  .glass-card-title {
    font-size: 12px;
    color: #475569;
    font-weight: 600;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .glass-card-value-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .glass-card-value {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    font-family: 'Outfit', 'Segoe UI', sans-serif;
  }

  .mini-chart {
    color: #10b981;
  }
  .chart-path {
    stroke-dasharray: 70;
    stroke-dashoffset: 70;
    animation: drawChart 3.5s ease-out infinite alternate;
  }
  @keyframes drawChart {
    0%, 15% { stroke-dashoffset: 70; }
    85%, 100% { stroke-dashoffset: 0; }
  }

  .mini-progress {
    color: var(--theme-secondary);
    transform: rotate(-90deg);
    transition: color 1.2s ease;
  }
  .progress-path {
    animation: fillProgress 4s ease-in-out infinite alternate;
  }
  @keyframes fillProgress {
    0%, 15% { stroke-dashoffset: 50; }
    85%, 100% { stroke-dashoffset: 12; }
  }

  &.gc-1 {
    top: -20px;
    left: -85px;
    transform: translate3d(-30px, -20px, 50px) scale(0.85);

    .is-loaded & {
      opacity: 1;
      transform: translate3d(0, 0, 150px) scale(1.05);
      transition-delay: 0.7s, 0.7s, 0s, 0s;
    }

    &:hover {
      transform: translate3d(0, -6px, 160px) scale(1.1);
      border-color: rgba(var(--theme-primary-rgb), 0.4);
      box-shadow:
        0 20px 48px rgba(var(--theme-primary-rgb), 0.15),
        inset 0 0 12px rgba(255, 255, 255, 0.8);
    }
  }

  &.gc-2 {
    top: 30%;
    right: -85px;
    transform: translate3d(30px, 20px, 30px) scale(0.75);

    .glass-card-icon {
      background: linear-gradient(135deg, rgba(var(--theme-secondary-rgb), 0.12) 0%, rgba(var(--theme-secondary-rgb), 0.03) 100%);
      border-color: rgba(var(--theme-secondary-rgb), 0.15);
      color: var(--theme-secondary);
    }

    .is-loaded & {
      opacity: 1;
      transform: translate3d(0, 0, 130px) scale(0.95);
      transition-delay: 1s, 1s, 0s, 0s;
    }

    &:hover {
      transform: translate3d(0, -4px, 140px) scale(1.0);
      border-color: rgba(var(--theme-secondary-rgb), 0.4);
      box-shadow:
        0 20px 48px rgba(var(--theme-secondary-rgb), 0.15),
        inset 0 0 12px rgba(255, 255, 255, 0.8);
    }
  }

  &.gc-3 {
    bottom: -25px;
    left: -65px;
    transform: translate3d(-20px, 30px, 40px) scale(0.8);

    .glass-card-icon {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.03) 100%);
      border-color: rgba(16, 185, 129, 0.15);
      color: #10b981;
    }

    .is-loaded & {
      opacity: 1;
      transform: translate3d(0, 0, 140px) scale(1.0);
      transition-delay: 0.85s, 0.85s, 0s, 0s;
    }

    &:hover {
      transform: translate3d(0, -5px, 150px) scale(1.05);
      border-color: rgba(16, 185, 129, 0.4);
      box-shadow:
        0 20px 48px rgba(16, 185, 129, 0.15),
        inset 0 0 12px rgba(255, 255, 255, 0.8);
    }
  }
}

@keyframes scanLine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}



/* ==========================================================================
   5. 轮播指示器样式
   ========================================================================== */
.slider-indicators {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 16px;
  z-index: 10;

  opacity: 0;
  transform: translateX(-50%) translateY(10px);
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);

  .is-loaded & {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
    transition-delay: 0.9s;
  }
}

.indicator {
  width: 28px;
  height: 3.5px;
  background: rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 2px;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }

  &.active {
    background: var(--theme-primary);
    box-shadow: 0 2px 10px var(--theme-glow);
    width: 48px;
  }
}

/* ==========================================================================
   6. 响应式布局自适应处理
   ========================================================================== */
@media (max-width: 1024px) {
  .hero-section {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    --hologram-scale: 0.85;
  }

  // 移去移动端非必要的 Preserve-3D 容器层级，防范 WebKit 复杂的 3D 渲染排序层级导致闪烁
  .hero-3d-card,
  .hero-content-grid,
  .hologram-wrapper,
  .hologram-core-container {
    transform-style: flat !important;
  }

  // 停止在移动端开销极高的模糊滤镜动画，提升页面帧率并修复合成层闪烁
  .glow-orb {
    animation: none !important;
  }

  .hero-3d-card {
    height: auto;
  }

  .hero-content-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    gap: 40px;
    text-align: center;
    padding: 100px 30px 40px;
  }

  .text-content-wrapper {
    order: 1;
  }

  .slide-content-block {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .hologram-wrapper {
    order: 2;
    height: 380px;
    margin-bottom: 40px;
  }

  .hologram-core-container {
    width: 240px;
    height: 240px;
  }

  .cube-3d-wrapper {
    width: 130px;
    height: 130px;
    margin-top: -65px;
    margin-left: -65px;
  }

  .cube-face {
    width: 130px;
    height: 130px;
    &.face-front  { transform: rotateY(  0deg) translateZ(65px); }
    &.face-back   { transform: rotateY(180deg) translateZ(65px); }
    &.face-left   { transform: rotateY(-90deg) translateZ(65px); }
    &.face-right  { transform: rotateY( 90deg) translateZ(65px); }
    &.face-top    { transform: rotateX( 90deg) translateZ(65px); }
    &.face-bottom { transform: rotateX(-90deg) translateZ(65px); }
  }

  .glass-card {
    padding: 10px 14px;

    &.gc-1 {
      left: -50px;
      top: -15px;
      transform: translate3d(-30px, -20px, 40px) scale(0.75) rotateY(-10deg);

      .is-loaded & {
        transform: translate3d(0, 0, 110px) scale(0.85) rotateY(0deg);
      }

      &:hover {
        transform: translate3d(0, -4px, 120px) scale(0.9) rotateY(0deg);
      }
    }

    &.gc-2 {
      right: -55px;
      top: 30%;
      bottom: auto;
      transform: translate3d(30px, 20px, 30px) scale(0.7) rotateY(15deg);

      .is-loaded & {
        transform: translate3d(0, 0, 95px) scale(0.82) rotateY(0deg);
      }

      &:hover {
        transform: translate3d(0, -3px, 105px) scale(0.87) rotateY(0deg);
      }
    }

    &.gc-3 {
      left: -40px;
      right: auto;
      bottom: -20px;
      transform: translate3d(-20px, 20px, 35px) scale(0.72) rotateY(-5deg);

      .is-loaded & {
        transform: translate3d(0, 0, 105px) scale(0.85) rotateY(0deg);
      }

      &:hover {
        transform: translate3d(0, -4px, 115px) scale(0.9) rotateY(0deg);
      }
    }
  }

  .hero-title {
    font-size: 2.8rem;
  }

  .hero-actions {
    justify-content: center;
  }

  .hero-stats-grid {
    margin-top: 36px;
    max-width: 500px;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    --hologram-scale: 0.72;
  }

  .hologram-wrapper {
    height: 320px;
    margin-bottom: 30px;
  }
}

@media (max-width: 576px) {
  .hero-section {
    --hologram-scale: 0.62;
  }

  .hologram-wrapper {
    height: 280px;
    margin-bottom: 20px;
  }

  .hero-content-grid {
    padding: 80px 20px 40px;
    gap: 30px;
  }

  .hero-title {
    font-size: 2.1rem;
    margin-bottom: 18px;
  }

  .hero-subtitle {
    font-size: 1.05rem;
    margin-bottom: 32px;
  }

  .hero-actions {
    flex-direction: column;
    gap: 12px;
    width: 100%;

    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }

  .slider-indicators {
    bottom: 25px;
  }

  .hero-stats-grid {
    grid-template-columns: 1fr;
    width: 100%;
    gap: 12px;
    margin-top: 28px;
  }
}
</style>
